// Embedded Signup da Meta — o ÚNICO momento em que o cliente sai do bZapper.
//
// Dentro do popup ele escolhe (ou cria) a conta comercial e o número, e aceita
// a integração. De volta aqui recebemos um `code` de uso único, que o servidor
// troca por um token permanente. O app secret nunca chega ao navegador, e o
// cliente nunca vê um id da Meta.

/** Config que o servidor entrega (GET /official/signup-config). */
export interface SignupConfig {
  enabled: boolean;
  app_id: string;
  config_id: string;
  graph_version: string;
}

/** O que o popup devolve quando o cliente conclui. */
export interface SignupResult {
  code: string;
  waba_id: string;
  phone_number_id: string;
}

/** O cliente fechou o popup no meio — não é erro, não merece alarme na tela. */
export class SignupCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'SignupCancelled';
  }
}

interface FacebookSDK {
  init(params: { appId: string; version: string; autoLogAppEvents?: boolean; xfbml?: boolean }): void;
  login(
    cb: (r: { authResponse?: { code?: string } | null; status?: string }) => void,
    opts: Record<string, unknown>,
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSDK;
    fbAsyncInit?: () => void;
  }
}

const SDK_ID = 'facebook-jssdk';
let loading: Promise<FacebookSDK> | null = null;

/** Carrega o SDK da Meta uma vez só e devolve a instância pronta. */
function loadSDK(cfg: SignupConfig): Promise<FacebookSDK> {
  if (window.FB) return Promise.resolve(window.FB);
  if (loading) return loading;

  loading = new Promise<FacebookSDK>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({
        appId: cfg.app_id,
        version: cfg.graph_version,
        autoLogAppEvents: true,
        xfbml: false,
      });
      resolve(window.FB!);
    };
    const s = document.createElement('script');
    s.id = SDK_ID;
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.onerror = () => {
      loading = null;
      reject(new Error('sdk_unavailable'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

// A Meta manda os ids da conta escolhida por postMessage, e o code pelo callback
// do login. As duas metades chegam por caminhos diferentes: só juntando as duas
// dá para concluir a conexão.
//
// A checagem é por HOSTNAME, não por sufixo da string: `endsWith('facebook.com')`
// aceitaria `evilfacebook.com`, que é exatamente o domínio que um atacante
// registraria para injetar um waba_id nosso.
function fromMeta(origin: string): boolean {
  try {
    const h = new URL(origin).hostname;
    return h === 'facebook.com' || h.endsWith('.facebook.com');
  } catch {
    return false;
  }
}

/**
 * Abre o popup e resolve quando o cliente conclui. Rejeita com SignupCancelled
 * se ele desistir.
 */
export function launchSignup(cfg: SignupConfig): Promise<SignupResult> {
  return loadSDK(cfg).then(
    (FB) =>
      new Promise<SignupResult>((resolve, reject) => {
        let picked: { waba_id: string; phone_number_id: string } | null = null;
        let cancelled = false;

        const onMessage = (ev: MessageEvent) => {
          if (!fromMeta(ev.origin)) return;
          try {
            const msg = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
            if (msg?.type !== 'WA_EMBEDDED_SIGNUP') return;
            if (typeof msg.event === 'string' && msg.event.startsWith('FINISH')) {
              picked = {
                waba_id: String(msg.data?.waba_id ?? ''),
                phone_number_id: String(msg.data?.phone_number_id ?? ''),
              };
            } else if (msg.event === 'CANCEL' || msg.event === 'ERROR') {
              cancelled = true;
            }
          } catch {
            /* mensagem de outra feature da Meta — ignorar */
          }
        };
        window.addEventListener('message', onMessage);

        const done = (fn: () => void) => {
          window.removeEventListener('message', onMessage);
          fn();
        };

        FB.login(
          (r) => {
            const code = r?.authResponse?.code;
            if (!code || cancelled) {
              done(() => reject(new SignupCancelled()));
              return;
            }
            // Sem os ids não há o que conectar: a Meta os envia por postMessage
            // e, em fluxos antigos, pode não enviar. O servidor ainda tenta
            // descobrir o número pela WABA, mas sem waba_id não há caminho.
            done(() =>
              resolve({
                code,
                waba_id: picked?.waba_id ?? '',
                phone_number_id: picked?.phone_number_id ?? '',
              }),
            );
          },
          // Parâmetros do Embedded Signup v4 (o v2 sai de circulação em
          // 15/10/2026). `extras.setup` vazio deixa o cliente escolher ou criar
          // a conta e o número dentro do popup.
          {
            config_id: cfg.config_id,
            response_type: 'code',
            override_default_response_type: true,
            extras: { setup: {} },
          },
        );
      }),
  );
}
